/**
 * GSM-7 / UCS-2 SMS encoding detection + segment counting.
 *
 * GSM-7 standard alphabet (each char = 1 septet):
 *   A-Z a-z 0-9 + punctuation + a handful of European accented letters
 *   + Greek capitals Δ Φ Γ Λ Ω Π Ψ Σ Θ Ξ
 *
 * GSM-7 "extended" alphabet (each char = 2 septets):
 *   | ^ € { } [ ] ~ \ + form feed
 *
 * If even one character falls outside both sets, the whole message must be
 * UCS-2 encoded (70 chars per single segment, 67 per multi-segment).
 */

// Basic GSM-7 set (single-septet chars).
const GSM7_BASIC = new Set<string>([
  "@", "£", "$", "¥", "è", "é", "ù", "ì", "ò", "Ç", "\n", "Ø", "ø", "\r", "Å", "å",
  "Δ", "_", "Φ", "Γ", "Λ", "Ω", "Π", "Ψ", "Σ", "Θ", "Ξ", "Æ", "æ", "ß", "É",
  " ", "!", '"', "#", "¤", "%", "&", "'", "(", ")", "*", "+", ",", "-", ".", "/",
  "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", ":", ";", "<", "=", ">", "?",
  "¡",
  "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O",
  "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z", "Ä", "Ö", "Ñ", "Ü", "§",
  "¿",
  "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o",
  "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z", "ä", "ö", "ñ", "ü", "à",
]);

// Extended GSM-7 set — chars that count as 2 septets each.
const GSM7_EXTENDED = new Set<string>([
  "\f", "^", "{", "}", "\\", "[", "~", "]", "|", "€",
]);

export type SmsEncoding = "gsm7" | "ucs2";

export interface SmsLengthInfo {
  encoding: SmsEncoding;
  /** Effective character count (with GSM-7 extended chars counted as 2). */
  units: number;
  /** Raw .length of the string. */
  rawLength: number;
  /** Number of SMS segments needed. */
  segments: number;
  /** Capacity per single segment for the chosen encoding. */
  singleCap: number;
  /** Capacity per segment when message spans multiple. */
  multiCap: number;
  /** Non-GSM-7 chars present in the input (for UI warning). */
  nonGsmChars: string[];
}

export function detectEncoding(text: string): SmsEncoding {
  for (const ch of text) {
    if (!GSM7_BASIC.has(ch) && !GSM7_EXTENDED.has(ch)) return "ucs2";
  }
  return "gsm7";
}

export function measureSms(text: string): SmsLengthInfo {
  const encoding = detectEncoding(text);
  const nonGsmChars: string[] = [];
  let units = 0;

  if (encoding === "gsm7") {
    for (const ch of text) {
      units += GSM7_EXTENDED.has(ch) ? 2 : 1;
    }
  } else {
    for (const ch of text) {
      if (!GSM7_BASIC.has(ch) && !GSM7_EXTENDED.has(ch)) {
        if (!nonGsmChars.includes(ch)) nonGsmChars.push(ch);
      }
    }
    // UCS-2 uses code units (2 bytes each). JS .length already counts UTF-16
    // code units, so it matches what carriers count.
    units = text.length;
  }

  const singleCap = encoding === "gsm7" ? 160 : 70;
  const multiCap = encoding === "gsm7" ? 153 : 67;
  const segments = units <= singleCap ? 1 : Math.ceil(units / multiCap);

  return {
    encoding,
    units,
    rawLength: text.length,
    segments,
    singleCap,
    multiCap,
    nonGsmChars,
  };
}
